import React, { useEffect, useMemo, useRef, useState } from 'react';
import useSize from '@react-hook/size';
import styled from 'styled-components';
import { metIntegration } from '@metservicedev/platforms-services-interface/src/services/metIntegration/metIntegration';
import { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { usePlatStageContext } from '@mono/use-plat-stage/src/use-plat-stage';
import { RunwayWindSpeedSettings } from '@mono/types/src/runway-widgets';
import { InfoPopover, IssueTime } from '@mono/metui';
import { convertDirectionToCardinal } from '@mono/direction-to-cardinal';
import { toHash } from '@mono/to-hash';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';

interface sensorCol {
    index: number;
    oneMinAvg?: number;
    oneMinDirection?: number;
    tenMinGust?: number;
    tenMinAvg?: number;
    tenMinLull?: number;
}

interface aviationAPIRespsonse {
    'met:station-id': string;
    'met:product-name': '1minuteobservation';
    'met:domain': 'aviation';
    'met:issue-time': string;
    'met:valid-at': string;
    'av:airport-location': string;
    'met:data': {
        airTemperature01HourMax: string;
        airTemperature01HourMin: string;
        airTemperature01MinuteAverage: string;
        cloud1Amount30MinuteAverage: string;
        cloud1Height30MinuteAverage: string;
        cloud2Amount30MinuteAverage: string;
        cloud2Height30MinuteAverage: string;
        cloud3Amount30MinuteAverage: string;
        cloud3Height30MinuteAverage: string;
        dewTemperature01MinuteAverage: string;
        equipmentTemperature01MinuteAverage: string;
        observationTimestamp: string;
        powerVoltage01MinuteAverage: string;
        presentWeatherNumeric115MinuteAverage: string;
        presentWeatherString115MinuteAverage: string;
        pressureMSL01HourMax: string;
        pressureMSL01HourMin: string;
        pressureMSL01MinuteAverage: string;
        pressureQFE01HourMax: string;
        pressureQFE01HourMin: string;
        pressureQFE01MinuteAverage: string;
        pressureQNH01HourMax: string;
        pressureQNH01HourMin: string;
        pressureQNH01MinuteAverage: string;
        pressureSensor01HourChg: string;
        pressureSensor01HourMax: string;
        pressureSensor01HourMin: string;
        pressureSensor01MinuteAverage: string;
        rainfall01HourAccumulation: string;
        rainfall01HourMax: string;
        rainfall01MinuteAccumulation: string;
        rainfall10MinuteAccumulation: string;
        rainfall10MinuteMax: string;
        relativeHumidity01HourMax: string;
        relativeHumidity01HourMin: string;
        relativeHumidity01MinuteAverage: string;
        stationAlphaId: string;
        stationNumericId: string;
        stationStatusType: string;
        visibility01MinuteAverage: string;
        visibility10MinuteAverage: string;
        windCCW01HourMax: string;
        windCCW01MinuteMax: string;
        windCCW10MinuteMax: string;
        windCW01HourMax: string;
        windCW01MinuteMax: string;
        windCW10MinuteMax: string;
        windDirection01HourAverage: string;
        windDirection01MinuteAverage: string;
        windDirection10MinuteAverage: string;
        windGust01HourDirection: string;
        windGust01HourMax: string;
        windGust01HourTime: string;
        windGust01MinuteMax: string;
        windGust10MinuteMax: string;
        windLull01MinuteMin: string;
        windLull10MinuteMin: string;
        windRun01HourAccumulation: string;
        windSpeed01HourAverage: string;
        windSpeed01MinuteAverage: string;
        windSpeed10MinuteAverage: string;
        windCCWM01HourMax: string;
        windCCWM01MinuteMax: string;
        windCCWM10MinuteMax: string;
        windCWM01HourMax: string;
        windCWM01MinuteMax: string;
        windCWM10MinuteMax: string;
        windDirectionM01HourAverage: string;
        windDirectionM01MinuteAverage: string;
        windDirectionM10MinuteAverage: string;
        windGustM01HourDirection: string;
    };
}

const HeaderCell = styled.div<{ mode?: 'cozy' | 'compact' }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 2px 0;
    white-space: nowrap;
    width: 100px;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].h4}
    &:last-child {
        border-bottom: none;
    }
`;

const TableCell = styled.p<{ mode?: 'cozy' | 'compact' }>`
    flex: 1;
    justify-content: center;
    white-space: nowrap;
    margin-top: 6px;
    margin-bottom: 10px;
    width: 100%;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p}
    line-height: 24px;
`;

const StyledText = styled.text<{ mode?: 'cozy' | 'compact' }>`
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p}
`;

const StyledPointerText = styled.text<{ mode?: 'cozy' | 'compact' }>`
    ${({ mode }) => TextStyles[mode ?? 'cozy'].h2}
`;

const Hr = styled.hr`
    border: none;
    border-bottom: 1px solid #cdcdcd;
    width: 100%;
`;

const toolbarStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
} as React.CSSProperties;

const getGradient = (colour?: string) => {
    switch (colour) {
        case '#FA6161':
            return { light: '#FA6161', dark: '#E93F3F' };
        case '#FF981B':
            return { light: '#FF981B', dark: '#CE7100' };
        case '#74A30F':
            return { light: '#74A30F', dark: '#577E00' };
        default:
            return { light: '#CDCDCD', dark: '#A8A8A8' };
    }
};

interface IWindSpeedBar extends sensorCol {
    windSpeedHeight: number;
    windSpeedWidth: number;
    thresholds: { value: number; colour: string }[];
    defaultColour: string;
    theme: { text: string; arrow: string; arrowText: string };
    mode: 'cozy' | 'compact';
}

const WindSpeedBar = ({
    oneMinAvg,
    tenMinAvg,
    tenMinGust,
    tenMinLull,
    index,
    windSpeedHeight,
    windSpeedWidth,
    thresholds,
    defaultColour,
    theme,
    mode = 'cozy',
}: IWindSpeedBar) => {
    const barError = oneMinAvg !== undefined && tenMinAvg !== undefined && tenMinGust !== undefined && tenMinLull !== undefined;
    const svgHeight = windSpeedHeight - 15 - 16;
    const svgWidth = Math.max(windSpeedWidth - 91, 16);
    const unitHeight = svgHeight / 70;
    const colour =
        tenMinGust !== undefined
            ? (thresholds.sort((a, b) => b.value - a.value).find((threshold) => threshold.value < tenMinGust)?.colour ?? defaultColour)
            : '';
    const cappedOneMinAvg = Math.min(oneMinAvg ?? 0, 70);

    return (
        <g>
            <linearGradient id={`max-gradient-${index}`} x1='0%' y1='0%' x2='0%' y2='100%'>
                <stop offset='0%' stopColor={getGradient(colour).dark} />
                <stop offset='70%' stopColor={getGradient(colour).light} />
            </linearGradient>
            {barError ? (
                <rect
                    x={3 + 45}
                    y={svgHeight - tenMinGust * unitHeight + 10}
                    height={tenMinGust * unitHeight - tenMinLull * unitHeight}
                    fill={`url(#max-gradient-${index})`}
                    rx={4}
                    width={svgWidth - 6}
                />
            ) : (
                <rect
                    x={45}
                    y={svgHeight - 70 * unitHeight + 10 + 3}
                    height={70 * unitHeight - 6}
                    width={svgWidth}
                    fill={`url(#max-gradient-${index})`}
                    rx={4}
                />
            )}

            {barError && (
                <g>
                    <svg
                        x={mode === 'cozy' ? -14 : 10}
                        y={svgHeight - cappedOneMinAvg * unitHeight - 1}
                        width={mode === 'cozy' ? '70' : '45'}
                        height={mode === 'cozy' ? '34' : '22'}
                        viewBox={mode === 'cozy' ? '0 0 50 34' : '0 0 45 22'}
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        {mode === 'cozy' ? (
                            <path
                                d='M34.7079 33.4476L49.0904 16.0971C49.6025 15.4794 49.549 14.5706 48.9679 14.0172L34.6922 0.421183C34.4083 0.150808 34.0313 1.74263e-06 33.6393 1.70346e-06L4.5803 -1.19948e-06C2.05067 -1.45218e-06 6.52958e-06 2.05066 6.27687e-06 4.58029L3.79546e-06 29.4197C3.54275e-06 31.9493 2.05066 34 4.58029 34L33.5324 34C33.9869 34 34.4178 33.7975 34.7079 33.4476Z'
                                fill={theme.arrow}
                            />
                        ) : (
                            <path
                                d='M26.3001 21.6332L34.4819 11.6332C34.7833 11.2649 34.7833 10.7351 34.4819 10.3668L26.3001 0.366761C26.1102 0.134626 25.8261 -8.02012e-07 25.5261 -8.28233e-07L3 -2.79753e-06C1.34315 -2.94238e-06 1.80588e-06 1.34314 1.66103e-06 3L2.62268e-07 19C1.17422e-07 20.6569 1.34315 22 3 22L25.5261 22C25.8261 22 26.1101 21.8654 26.3001 21.6332Z'
                                fill={theme.arrow}
                            />
                        )}
                    </svg>
                    <StyledPointerText
                        mode={mode}
                        x={mode === 'cozy' ? 18 : 24}
                        y={mode === 'cozy' ? svgHeight - cappedOneMinAvg * unitHeight + 26 : svgHeight - cappedOneMinAvg * unitHeight + 16}
                        fill={theme.arrowText}
                        textAnchor='middle'
                    >
                        {oneMinAvg}
                    </StyledPointerText>
                </g>
            )}
            <rect x={0 + 45} y={10} width={svgWidth} height={svgHeight} stroke={theme.text} strokeWidth={1} rx={2} fill={'none'} />

            {[0, 10, 20, 30, 40, 50, 60, 70].map((num) => {
                return (
                    <g key={`${index}-${num}`}>
                        <StyledText mode={mode} x={svgWidth + 9 + 45} y={svgHeight - num * unitHeight + 10} dominantBaseline='middle' fill={theme.text}>
                            {num === 70 ? '70+' : num}
                        </StyledText>
                        {num === 0 || num === 70 ? null : (
                            <line
                                x1={0 + 45}
                                y1={svgHeight - num * unitHeight + 10}
                                x2={svgWidth + 6 + 45}
                                y2={svgHeight - num * unitHeight + 10}
                                stroke={theme.text}
                                opacity={0.2}
                            />
                        )}
                        {num === 70 ? null : (
                            <line
                                x1={svgWidth + 45}
                                x2={svgWidth + 6 + 45}
                                y1={svgHeight - (num + 5) * unitHeight + 10}
                                y2={svgHeight - (num + 5) * unitHeight + 10}
                                stroke={theme.text}
                                opacity={0.2}
                            />
                        )}
                    </g>
                );
            })}
            {barError && (
                <g>
                    <line
                        x1={0 + 45}
                        x2={svgWidth + 45}
                        y1={svgHeight - tenMinAvg * unitHeight + 10}
                        y2={svgHeight - tenMinAvg * unitHeight + 10}
                        strokeWidth={4}
                        stroke={theme.text}
                    />
                </g>
            )}
        </g>
    );
};

export const RunwayWindSpeed = ({ settings, theme: themeName }: WeatherWidgetRenderProps<RunwayWindSpeedSettings>) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const [width, height] = useSize(chartRef);
    const { stage } = usePlatStageContext();

    const [dataKey, setDataKey] = useState<string>(toHash('this is because the rows dont have unique IDs. Using the sensor as the ID is not good.'));

    const { aerodrome, title, timezone, weatherStations, displayTable, thresholds, defaultColour } = settings;
    const [sensorData, setSensorData] = useState<{ [sensor: string]: aviationAPIRespsonse }>({});

    const targetTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const isDarkMode = themeName === 'dark';
    const theme = isDarkMode
        ? {
              text: '#ffffff',
              arrow: '#ffffff',
              arrowText: '#172344',
              issueTime: '#ffffff',
          }
        : {
              text: '#172344',
              arrow: '#373737',
              arrowText: '#ffffff',
              issueTime: '#595959',
          };

    useEffect(() => {
        const metjwt = localStorage.getItem('metjwt');
        const { metserviceApi } = metIntegration;

        let timeoutHook = 0;
        const abortController = new AbortController();

        const patientQuery = async () => {
            try {
                const headers: {} | { authorization: string } = metjwt
                    ? {
                          authorization: 'Bearer ' + metjwt,
                      }
                    : {};
                const promises = weatherStations.map(({ sensor }) => {
                    return fetch(`${metserviceApi[stage]}/aviation/observations/airport/${aerodrome}/weatherStation/${sensor}/latest`, {
                        headers,
                        signal: abortController.signal,
                    })
                        .then((res) => {
                            return res.json();
                        })
                        .then((data) => {
                            const something = data?.response?.[0];
                            setSensorData((prev) => ({ ...prev, [sensor]: something }));
                        })
                        .catch((e) => {
                            console.warn(e);
                            if (abortController.signal.aborted === true) return;
                            setSensorData((prev) => {
                                delete prev[sensor];
                                return prev;
                            });
                        });
                });
                await Promise.all(promises);
            } catch (e) {
                console.error(e);
            } finally {
                setDataKey((prev) => toHash(prev));
                timeoutHook = setTimeout(patientQuery, 55 * 1000) as unknown as number;
            }
        };

        patientQuery();

        return () => {
            abortController.abort();
            clearTimeout(timeoutHook);
        };
    }, [toHash(settings.weatherStations)]);

    const tableData = useMemo(() => {
        const tableData = weatherStations.map(({ sensor }, index) => {
            const apiResponse = sensorData[sensor];
            if (!apiResponse || !apiResponse['met:data']) {
                return { index, oneMinAvg: undefined, oneMinDirection: undefined, tenMinAvg: undefined, tenMinGust: undefined, tenMinLull: undefined };
            }
            const data: aviationAPIRespsonse['met:data'] = apiResponse['met:data'];
            const {
                windSpeed01MinuteAverage: oneMinAvg,
                windDirection01MinuteAverage: oneMinDirection,
                windSpeed10MinuteAverage: tenMinAvg,
                windDirection10MinuteAverage: tenMinDirection,
                windGust10MinuteMax: tenMinGust,
                windLull10MinuteMin: tenMinLull,
            } = data;
            return { index, oneMinAvg, oneMinDirection, tenMinAvg, tenMinDirection, tenMinGust, tenMinLull };
        });

        return tableData;
    }, [dataKey]);
    const issueTime = useMemo(() => {
        const issueTimes = Object.values(sensorData)
            .map((s) => s?.['met:issue-time'])
            .filter((s): s is string => !!s);
        if (issueTimes.length === 0) return undefined;

        return issueTimes.sort((a, b) => (a > b ? -1 : 1))[0];
    }, [dataKey]);

    const windSpeedWidth = width / (tableData?.length ? tableData.length : 1) - 24;
    const windSpeedHeight = height - 14 - 32 - 12 - 32 + 26 - (displayTable ? 194 : 24);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                minWidth: tableData?.length * (settings.mode === 'cozy' ? 106 : 96) + 100,
                minHeight: 200,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                font: '400 12px/18px Open Sans',
                color: theme.text,
            }}
            ref={chartRef}
        >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {/* Title */}
                <div style={toolbarStyle}>
                    <div style={{ fontSize: 16, lineHeight: '22px', fontWeight: 700 }}>{title}</div>
                </div>
                {/* Infopopeover */}
                <div style={{ display: 'flex', columnGap: 4 }}>
                    {issueTime && (
                        <IssueTime
                            issueTime={issueTime}
                            targetTimezone={targetTimezone}
                            style={toolbarStyle}
                            colour={theme.issueTime}
                            isDarkMode={isDarkMode}
                        />
                    )}
                    <div style={toolbarStyle}>
                        <InfoPopover>
                            <div style={{ padding: '5px 16px', color: '#595959' }}>
                                All wind data is shown in knots.
                                <br />
                                <div style={{ paddingTop: 8 }}>
                                    <b>Arrow:</b> Wind speed average for the last 1 minute.
                                </div>
                                <div style={{ paddingTop: 8 }}>
                                    <b>Horizontal line:</b> Wind speed average for the last 10 minutes.
                                </div>
                                <div style={{ paddingTop: 8 }}>
                                    <b>Coloured bar:</b> Maximum wind speed (gust) and minimum wind speed (lull) for the last 10 minutes. The colour of the bar
                                    corresponds to the maximum gust threshold.
                                </div>
                                <div style={{ display: 'flex', paddingTop: 8 }}>
                                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M7.5 3.75C7.08579 3.75 6.75 4.08579 6.75 4.5V19.5H4.5C4.08579 19.5 3.75 19.8358 3.75 20.25C3.75 20.6642 4.08579 21 4.5 21H7.5H10.5C10.9142 21 11.25 20.6642 11.25 20.25C11.25 19.8358 10.9142 19.5 10.5 19.5H8.25V4.5C8.25 4.08579 7.91421 3.75 7.5 3.75Z'
                                            fill='#909AB5'
                                        />
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M18.8495 4.96024L9.84945 3.75617C9.81649 3.75175 9.78326 3.74954 9.75 3.74954C9.33579 3.74954 9 4.08533 9 4.49954V9.75084C9 9.80039 9.00491 9.84982 9.01466 9.89841C9.09616 10.3045 9.49145 10.5677 9.89757 10.4862L18.8976 8.68006C19.2479 8.60976 19.5 8.30205 19.5 7.94472V5.70362C19.5 5.32784 19.2219 5.01007 18.8495 4.96024ZM18 7.33028L10.5 8.83538V5.35656L18 6.35996V7.33028Z'
                                            fill='#909AB5'
                                        />
                                        <rect opacity='0.3' x='15' y='5.25' width='3' height='3.75' fill='#909AB5' />
                                        <rect x='13.5' y='5.25' width='1.5' height='3.75' fill='#909AB5' />
                                    </svg>

                                    <div style={{ paddingLeft: 8, paddingBottom: 8 }}>
                                        <b>MAXMIMUM GUST THRESHOLDS</b>
                                        <br />
                                        <b>None:</b> 0 knots
                                        <br />
                                        <b style={{ color: '#598200' }}>Green:</b> 1-19 knots
                                        <br />
                                        <b style={{ color: '#AA6600' }}>Orange:</b> 20-29 knots
                                        <br />
                                        <b style={{ color: '#D83D3D' }}>Red:</b> 30 knots
                                        <br />
                                        <b>Grey:</b> Sensor not working
                                    </div>
                                </div>
                            </div>
                        </InfoPopover>
                    </div>
                </div>
            </div>
            {/* Divider */}
            <hr style={{ width: '100%', border: 'none', borderBottom: '1px solid #cdcdcd' }} />
            {
                <>
                    {/* Bars container */}
                    <div style={{ display: 'flex', flex: 1, paddingTop: 16 }}>
                        {displayTable && <div style={{ width: 100 }} />}
                        {tableData?.map(({ index, oneMinAvg, tenMinAvg, tenMinGust, tenMinLull }) => {
                            const sensorNumbers = {
                                index,
                                oneMinAvg: oneMinAvg ? parseFloat(oneMinAvg) : undefined,
                                tenMinAvg: tenMinAvg ? Math.min(70, parseFloat(tenMinAvg)) : undefined,
                                tenMinGust: tenMinGust ? Math.min(70, parseFloat(tenMinGust)) : undefined,
                                tenMinLull: tenMinLull ? Math.min(70, parseFloat(tenMinLull)) : undefined,
                            };

                            return (
                                <svg key={index} height={windSpeedHeight} width={windSpeedWidth} style={{ flex: 1 }}>
                                    <WindSpeedBar
                                        {...{ ...sensorNumbers, windSpeedHeight, windSpeedWidth, thresholds, defaultColour, theme }}
                                        mode={settings.mode}
                                    />
                                </svg>
                            );
                        })}
                    </div>
                    {/* data table container */}
                    <div style={{ display: 'flex' }}>
                        {displayTable && (
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', position: 'absolute' }}>
                                <Hr style={{ marginTop: '40px' }} />
                                <Hr style={{ marginTop: '30px' }} />
                                <Hr style={{ marginTop: '36px' }} />
                                <Hr style={{ marginTop: '34px' }} />
                            </div>
                        )}
                        {displayTable && (
                            <div style={{ width: 100, display: 'flex', flexDirection: 'column' }}>
                                <HeaderCell mode={settings.mode}>SENSOR</HeaderCell>
                                <HeaderCell mode={settings.mode}>1 MIN AVG</HeaderCell>
                                <HeaderCell mode={settings.mode}>10 MIN AVG</HeaderCell>
                                <HeaderCell mode={settings.mode}>10 MIN GUST</HeaderCell>
                                <HeaderCell mode={settings.mode}>10 MIN LULL</HeaderCell>
                            </div>
                        )}

                        {tableData?.map((sensor, index) => {
                            return (
                                <div
                                    key={`Table-${index}-${sensor.index}`}
                                    style={{ width: windSpeedWidth, display: 'flex', flexDirection: 'column', flex: 12, fontSize: 14, textAlign: 'center' }}
                                >
                                    <TableCell mode={settings.mode}>{weatherStations[index]?.name ?? 'N/A'}</TableCell>
                                    {displayTable && (
                                        <>
                                            <TableCell mode={settings.mode}>
                                                {sensor.oneMinAvg ? `${sensor.oneMinAvg} ${convertDirectionToCardinal(sensor.oneMinDirection)}` : 'N/A'}
                                            </TableCell>
                                            <TableCell mode={settings.mode}>
                                                {sensor.tenMinDirection ? `${sensor.tenMinAvg} ${convertDirectionToCardinal(sensor?.tenMinDirection)}` : 'N/A'}
                                            </TableCell>
                                            <TableCell mode={settings.mode}>{sensor.tenMinGust ?? 'N/A'}</TableCell>
                                            <TableCell mode={settings.mode}>{sensor.tenMinLull ?? 'N/A'}</TableCell>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            }
        </div>
    );
};
